import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Table, 
  Tag, 
  Space, 
  Button, 
  Input, 
  DatePicker, 
  Select, 
  message, 
  Popconfirm,
  Tooltip,
  Card,
  Empty
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  SmileOutlined,
  FrownOutlined,
  MehOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { emotionRecordApi, emotionApi } from '../services/api';
import locale from 'antd/es/date-picker/locale/zh_CN';
import { formatDate } from '../utils/dateUtils';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const EmotionRecordsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [emotions, setEmotions] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  
  // 过滤条件
  const [filters, setFilters] = useState({
    keyword: '',
    dateRange: null,
    emotionId: null
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [emotionsResponse, recordsResponse] = await Promise.all([
          emotionApi.getAllEmotions(),
          emotionRecordApi.getUserEmotionRecords(currentUser.id)
        ]);
        
        setEmotions(emotionsResponse.data);
        
        // 处理记录，确保时间格式正确
        const processedRecords = recordsResponse.data.map(record => {
          // 为调试输出记录内容
          console.log("原始记录:", record);
          return {
            ...record,
            // 确保情绪对象存在
            emotion: record.emotion || { name: '未知', intensity: record.intensity || 5 }
          };
        });
        
        setRecords(processedRecords);
        setFilteredRecords(processedRecords);
      } catch (error) {
        console.error('获取数据失败:', error);
        message.error('获取情绪记录数据失败');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser]);

  // 根据过滤条件更新显示的记录
  useEffect(() => {
    let result = [...records];
    
    // 关键词过滤
    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase();
      result = result.filter(record => 
        (record.event && record.event.toLowerCase().includes(keyword)) ||
        (record.emotion && record.emotion.name.toLowerCase().includes(keyword))
      );
    }
    
    // 日期范围过滤
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      result = result.filter(record => {
        const recordDate = new Date(record.createdAt);
        return recordDate >= startDate && recordDate <= endDate;
      });
    }
    
    // 情绪类型过滤
    if (filters.emotionId) {
      result = result.filter(record => 
        record.emotion && record.emotion.id === filters.emotionId
      );
    }
    
    setFilteredRecords(result);
  }, [filters, records]);

  // 处理删除情绪记录
  const handleDelete = async (id) => {
    try {
      await emotionRecordApi.deleteEmotionRecord(id);
      message.success('情绪记录已删除');
      
      // 更新本地数据
      const updatedRecords = records.filter(record => record.id !== id);
      setRecords(updatedRecords);
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除情绪记录失败');
    }
  };

  // 获取情绪标签颜色
  const getEmotionTagColor = (intensity) => {
    if (!intensity && intensity !== 0) return '';
    if (intensity > 7) return 'success';
    if (intensity > 5) return 'processing';
    if (intensity < 3) return 'error';
    if (intensity < 5) return 'warning';
    return 'default';
  };

  // 获取情绪图标
  const getEmotionIcon = (intensity) => {
    if (!intensity && intensity !== 0) return null;
    if (intensity > 5) return <SmileOutlined />;
    if (intensity < 5) return <FrownOutlined />;
    return <MehOutlined />;
  };
  
  // 表格列配置
  const columns = [
    {
      title: '情绪',
      dataIndex: 'emotion',
      key: 'emotion',
      render: (emotion) => (
        <Tag 
          color={getEmotionTagColor(emotion?.intensity)} 
          icon={getEmotionIcon(emotion?.intensity)}
        >
          {emotion?.name || '未知'}
        </Tag>
      ),
      sorter: (a, b) => {
        if (!a.emotion || !b.emotion) return 0;
        return a.emotion.intensity - b.emotion.intensity;
      },
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: '事件描述',
      dataIndex: 'event',
      key: 'event',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text || '无事件描述'}>
          <span>{text || '无事件描述'}</span>
        </Tooltip>
      )
    },
    {
      title: '身体反应',
      dataIndex: 'physicalResponse',
      key: 'physicalResponse',
      ellipsis: true,
      responsive: ['md'],
      render: (text) => (
        <Tooltip title={text || '无记录'}>
          <span>{text || '无记录'}</span>
        </Tooltip>
      )
    },
    {
      title: '日期',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDate(date),
      sorter: (a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      },
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'descend',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => navigate(`/emotion-records/${record.id}`)}
          >
            查看
          </Button>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => navigate(`/emotion-records/${record.id}/edit`)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条情绪记录吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 处理关键词搜索
  const handleKeywordChange = (e) => {
    setFilters({...filters, keyword: e.target.value});
  };

  // 处理日期范围选择
  const handleDateRangeChange = (dates) => {
    setFilters({...filters, dateRange: dates});
  };

  // 处理情绪类型选择
  const handleEmotionChange = (value) => {
    setFilters({...filters, emotionId: value});
  };

  // 重置过滤器
  const resetFilters = () => {
    setFilters({
      keyword: '',
      dateRange: null,
      emotionId: null
    });
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2}>情绪记录</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => navigate('/emotion-records/create')}
        >
          新建记录
        </Button>
      </div>
      
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <Input
            placeholder="搜索事件或情绪"
            value={filters.keyword}
            onChange={handleKeywordChange}
            style={{ width: 200 }}
            prefix={<SearchOutlined />}
            allowClear
          />
          
          <RangePicker 
            locale={locale}
            value={filters.dateRange}
            onChange={handleDateRangeChange}
            style={{ width: 300 }}
          />
          
          <Select
            placeholder="选择情绪类型"
            value={filters.emotionId}
            onChange={handleEmotionChange}
            style={{ width: 150 }}
            allowClear
          >
            {emotions.map(emotion => (
              <Option key={emotion.id} value={emotion.id}>
                {emotion.name}
              </Option>
            ))}
          </Select>
          
          <Button onClick={resetFilters}>重置</Button>
        </div>
      </Card>
      
      <Table
        columns={columns}
        dataSource={filteredRecords}
        rowKey="id"
        loading={loading}
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        locale={{ 
          emptyText: <Empty description="暂无情绪记录" /> 
        }}
      />
    </div>
  );
};

export default EmotionRecordsPage; 